import { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/button';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border container mx-auto pl-2 pr-2 max-w-4xl pb-8 md:pb-16`}
`;

const FormContainer = styled.div`
  ${tw`bg-white border-2 border-black p-2 box-border`}
  font-family: 'Times New Roman', serif;
`;

const FormHeader = styled.div`
  ${tw`border-b-2 border-black pb-3 mb-4 text-center`}
`;

const FormTitle = styled.h2`
  ${tw`text-2xl font-bold mb-4`}
  letter-spacing: 2px;
`;

const ImportantNotice = styled.div`
  ${tw`text-xs leading-tight`}
  font-weight: bold;
`;

const StrongText = styled.strong`
  ${tw`text-xs`}
`;

const LightText = styled.span`
  ${tw`text-xs font-light`}
`;

const EmText = styled.em`
  ${tw`italic`}
`;

const VehicleInfoSection = styled.div`
  ${tw`mb-2`}
`;

const VehicleInfoGrid = styled.div`
  ${tw`flex gap-4 mb-2`}
`;

const VehicleField = styled.div<{ width?: string }>`
  ${tw`flex flex-col w-full max-w-full min-w-0 box-border`}
  ${({ width }) => width && `flex: 0 0 ${width};`}
`;

const FieldLabel = styled.label`
  ${tw`text-xs`}
`;

const FieldInput = styled.input`
  ${tw`bg-transparent px-0 py-1 text-sm font-medium`}
  border: none;
  border-bottom: 1px solid black;
  width: 100%;
  overflow: visible;
  &:focus {
    ${tw`outline-none`}
    border-bottom: 1px solid blue;
  }
`;

const SectionTitle = styled.h3`
  ${tw`font-bold text-xs border-b border-black`}
  letter-spacing: 1px;
`;

const WarrantiesSection = styled.div`
  ${tw`mb-1`}
`;

const WarrantyOption = styled.div`
  ${tw`mb-1 border border-gray-300 pb-2`}
`;

const CheckboxRow = styled.div`
  ${tw`flex items-start mb-1`}
`;

const Checkbox = styled.input`
  ${tw`mr-2 mt-1`}
  width: 26px;
  height: 26px;
  border: 2px solid black;
`;

const WarrantyContent = styled.div`
  ${tw`flex-1`}
`;

const WarrantyTitle = styled.div`
  ${tw`font-bold mb-1`}

  font-size: 26px;
`;

const WarrantyDescription = styled.div`
  ${tw`leading-tight text-gray-800`}
  font-size: 8px;
`;

const SubWarrantyOptions = styled.div`
  ${tw`ml-8 mt-3`}
`;

const SubCheckboxRow = styled.div`
  ${tw`flex items-center`}
`;

const SubCheckbox = styled.input`
  ${tw`mr-3`}
  width: 14px;
  height: 14px;
`;

const SubLabel = styled.span`
  ${tw`text-xs font-medium`}
`;

const SystemsCoveredSection = styled.div`
  ${tw`mb-2 flex flex-col`}
`;

const SystemsGrid = styled.div`
  ${tw`flex gap-8 mb-2`}
  width: 100%;
`;

const SystemColumn = styled.div`
  ${tw`flex flex-col space-y-3`}
  width: 45%;
  flex: 0 0 45%;
`;

const SystemRow = styled.div`
  ${tw`w-full`}
`;

const SystemLine = styled.div`
  ${tw`border-b-2 border-black w-full border-solid border-t-0 border-l-0 border-r-0`}
  height: 10px;
  position: relative;
  min-width: 200px;
`;

const SystemInput = styled.input`
  ${tw`bg-transparent w-full px-1 py-1 text-sm absolute top-0 left-0`}
  border: none;
  height: 20px;
  &:focus {
    ${tw`outline-none`}
  }
`;

const DurationSection = styled.div`
  ${tw`flex items-center gap-4`}
`;

const DurationLabel = styled.span`
  ${tw`text-sm font-bold whitespace-nowrap`}
`;

const DurationLine = styled.div`
  ${tw`flex-1 border-b-2 border-black`}
  height: 24px;
  position: relative;
`;

const DurationInput = styled.input`
  ${tw`bg-transparent w-full px-1 py-1 text-sm absolute top-0 left-0`}
  border: none;
  height: 20px;
  &:focus {
    ${tw`outline-none`}
  }
`;

const NonDealerWarrantiesSection = styled.div`
  ${tw`mb-6`}
`;

const WarrantyCheckboxRow = styled.div`
  ${tw`flex items-start mb-1 text-xs`}
`;

const SmallCheckbox = styled.input`
  ${tw`mr-3 mt-1 flex-shrink-0`}
  width: 12px;
  height: 12px;
`;

const CheckboxText = styled.span`
  ${tw`leading-tight`}
`;

const AdditionalInfoSection = styled.div`
  ${tw`mb-6`}
`;

const InfoText = styled.div`
  ${tw`text-xs leading-tight text-gray-800`}
`;

const TextArea = styled.textarea`
  ${tw`border-2 border-black bg-transparent w-full px-2 py-2 text-xs h-24 resize-none`}
  &:focus {
    ${tw`outline-none border-blue-500`}
  }
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center gap-4 mt-8`}
  @media print {
    display: none;
  }
`;

const BackButton = styled.button`
  ${tw`
    px-6 py-2 
    bg-gray-500 text-white 
    rounded 
    transition-colors 
    duration-200
    cursor-pointer
    border-none
    font-medium
  `}
  width: 150px;
  height: 40px;
  &:hover {
    ${tw`bg-gray-600`}
  }
`;

const PrintButton = styled.button`
  ${tw`
    px-6 py-2 
    bg-blue-500 text-white 
    rounded 
    transition-colors 
    duration-200
    cursor-pointer
    border-none
    font-medium
  `}
  width: 150px;
  height: 40px;
  &:hover {
    ${tw`bg-blue-600`}
  }
`;

const GenerateButton = styled.button`
  ${tw`
    px-6 py-2 
    bg-green-500 text-white 
    rounded 
    transition-colors 
    duration-200
    cursor-pointer
    border-none
    font-medium
  `}
  width: 150px;
  height: 40px;

  &:hover {
    ${tw`bg-green-600`}
  }
`;

const PrintStyles = styled.div`
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body {
      margin: 0 !important;
      padding: 0 !important;
    }

    /* Hide navigation and other non-form elements */
    nav,
    header,
    .navigation,
    .navbar,
    .header {
      display: none !important;
    }

    /* Ensure form takes full page */
    ${PageContainer} {
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    ${FormContainer} {
      border: 2px solid black !important;
      background: white !important;
      box-shadow: none !important;
      margin: 0 !important;
      padding: 20px !important;
    }

    /* Ensure borders and lines print correctly */
    ${SystemLine}, ${DurationLine} {
      border-bottom: 2px solid black !important;
    }

    ${FieldInput} {
      border-bottom: 1px solid black !important;
    }

    /* Page break settings */
    page-break-inside: avoid;
    page-break-after: auto;
  }
`;

const WarrantyDocumentText = styled.div`
  ${tw`mt-4 text-xs leading-tight`}
`;

const ServiceContractRow = styled.div`
  ${tw`mt-3`}
`;

export default function BuyersGuidePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get URL parameters
  const agreementId = searchParams.get('agreementId');
  const year = searchParams.get('year');
  const make = searchParams.get('make');
  const model = searchParams.get('model');
  const vin = searchParams.get('vin');

  const [formData, setFormData] = useState({
    year: year || '',
    make: make || '',
    model: model || '',
    vin: vin || '',
    asIs: false,
    dealerWarranty: false,
    limitedWarranty: false,
    fullWarranty: false,
    systemsCovered: Array(8).fill(''),
    duration: '',
    manufacturerWarrantyApplies: false,
    manufacturerUsedWarrantyApplies: false,
    otherUsedWarrantyApplies: false,
    serviceContract: false,
    additionalInfo: '',
  });

  // Update form data when URL parameters change
  useEffect(() => {
    if (year || make || model || vin) {
      setFormData(prev => ({
        ...prev,
        year: year || prev.year,
        make: make || prev.make,
        model: model || prev.model,
        vin: vin || prev.vin,
      }));
    }
  }, [year, make, model, vin]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSystemChange = (index: number, value: string) => {
    const newSystems = [...formData.systemsCovered];
    newSystems[index] = value;
    setFormData(prev => ({
      ...prev,
      systemsCovered: newSystems,
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGenerate = () => {
    // Here you would typically generate a PDF or handle the form submission
    // For now, just show an alert
    alert("Buyer's Guide generated successfully!");
  };

  const handleBack = () => {
    navigate('/dealer');
  };

  return (
    <>
      <PrintStyles />
      <PageContainer>
        <FormContainer>
          <FormHeader>
            <FormTitle>BUYERS GUIDE</FormTitle>
            <ImportantNotice>
              <StrongText>IMPORTANT:</StrongText> Spoken promises are difficult to enforce. Ask that
              all dealer promises be put in writing. Keep this form.
            </ImportantNotice>
          </FormHeader>

          <VehicleInfoSection>
            <VehicleInfoGrid>
              <VehicleField width="20%">
                <FieldLabel>VEHICLE MAKE</FieldLabel>
                <FieldInput
                  value={formData.make}
                  onChange={e => handleInputChange('make', e.target.value)}
                  placeholder=""
                />
              </VehicleField>
              <VehicleField width="15%">
                <FieldLabel>MODEL</FieldLabel>
                <FieldInput
                  value={formData.model}
                  onChange={e => handleInputChange('model', e.target.value)}
                  placeholder=""
                />
              </VehicleField>
              <VehicleField width="10%">
                <FieldLabel>YEAR</FieldLabel>
                <FieldInput
                  value={formData.year}
                  onChange={e => handleInputChange('year', e.target.value)}
                  placeholder=""
                />
              </VehicleField>
              <VehicleField width="40%">
                <FieldLabel>VIN Number</FieldLabel>
                <FieldInput
                  value={formData.vin}
                  onChange={e => handleInputChange('vin', e.target.value)}
                  placeholder=""
                />
              </VehicleField>
            </VehicleInfoGrid>
          </VehicleInfoSection>

          <WarrantiesSection>
            <SectionTitle>WARRANTIES FOR THIS VEHICLE:</SectionTitle>

            <WarrantyOption>
              <CheckboxRow>
                <Checkbox
                  type="checkbox"
                  checked={formData.asIs}
                  onChange={e => handleInputChange('asIs', e.target.checked)}
                />
                <WarrantyContent>
                  <WarrantyTitle>AS IS - NO DEALER WARRANTY</WarrantyTitle>
                  <WarrantyDescription>
                    THE DEALER DOES NOT PROVIDE A WARRANTY FOR ANY REPAIRS AFTER SALE.
                  </WarrantyDescription>
                </WarrantyContent>
              </CheckboxRow>
            </WarrantyOption>

            <WarrantyOption>
              <CheckboxRow>
                <Checkbox
                  type="checkbox"
                  checked={formData.dealerWarranty}
                  onChange={e => handleInputChange('dealerWarranty', e.target.checked)}
                />
                <WarrantyContent>
                  <WarrantyTitle>DEALER WARRANTY</WarrantyTitle>
                  <WarrantyDescription>
                    <StrongText>FULL WARRANTY:</StrongText> The dealer will pay ___% of the labor
                    and ___% of the parts for the covered systems that fail during the warranty
                    period. Ask the dealer for a copy of the warranty, and for documents that
                    explain warranty coverage, exclusions, and the dealer's repair obligations.
                    Implied warranties under your state's law may give you additional rights.
                  </WarrantyDescription>

                  <SubWarrantyOptions>
                    <SubCheckboxRow>
                      <SubCheckbox
                        type="checkbox"
                        checked={formData.fullWarranty}
                        onChange={e => handleInputChange('fullWarranty', e.target.checked)}
                      />
                      <SubLabel>FULL WARRANTY</SubLabel>
                    </SubCheckboxRow>
                    <SubCheckboxRow>
                      <SubCheckbox
                        type="checkbox"
                        checked={formData.limitedWarranty}
                        onChange={e => handleInputChange('limitedWarranty', e.target.checked)}
                      />
                      <SubLabel>
                        LIMITED WARRANTY. The dealer will pay ___% of the labor and ___% of the
                        parts for the covered systems that fail during the warranty period. Ask the
                        dealer for a copy of the warranty, and for an explanation of warranty
                        coverage, exclusions, and repair obligations. Implied warranties under your
                        state's law may give you additional rights.
                      </SubLabel>
                    </SubCheckboxRow>
                  </SubWarrantyOptions>
                </WarrantyContent>
              </CheckboxRow>
            </WarrantyOption>
          </WarrantiesSection>

          <SystemsCoveredSection>
            <SystemsGrid>
              <SystemColumn>
                <SectionTitle>SYSTEMS COVERED:</SectionTitle>
                {Array.from({ length: 3 }, (_, index) => (
                  <SystemRow key={index}>
                    <SystemLine>
                      <SystemInput
                        value={formData.systemsCovered[index] || ''}
                        onChange={e => handleSystemChange(index, e.target.value)}
                        placeholder=""
                      />
                    </SystemLine>
                  </SystemRow>
                ))}
              </SystemColumn>
              <SystemColumn>
                <SectionTitle>DURATION:</SectionTitle>
                {Array.from({ length: 3 }, (_, index) => (
                  <SystemRow key={index}>
                    <SystemLine>
                      <SystemInput
                        value={formData.systemsCovered[index] || ''}
                        onChange={e => handleSystemChange(index, e.target.value)}
                        placeholder=""
                      />
                    </SystemLine>
                  </SystemRow>
                ))}
              </SystemColumn>
            </SystemsGrid>
          </SystemsCoveredSection>

          <NonDealerWarrantiesSection>
            <SectionTitle>NON-DEALER WARRANTIES FOR THIS VEHICLE:</SectionTitle>

            <WarrantyCheckboxRow>
              <SmallCheckbox
                type="checkbox"
                checked={formData.manufacturerWarrantyApplies}
                onChange={e => handleInputChange('manufacturerWarrantyApplies', e.target.checked)}
              />
              <CheckboxText>
                <StrongText>MANUFACTURER'S WARRANTY STILL APPLIES.</StrongText> The manufacturer's
                original warranty has not expired on some components of the vehicle.
              </CheckboxText>
            </WarrantyCheckboxRow>

            <WarrantyCheckboxRow>
              <SmallCheckbox
                type="checkbox"
                checked={formData.manufacturerUsedWarrantyApplies}
                onChange={e =>
                  handleInputChange('manufacturerUsedWarrantyApplies', e.target.checked)
                }
              />
              <CheckboxText>
                <StrongText>MANUFACTURER'S USED VEHICLE WARRANTY APPLIES.</StrongText>
              </CheckboxText>
            </WarrantyCheckboxRow>

            <WarrantyCheckboxRow>
              <SmallCheckbox
                type="checkbox"
                checked={formData.otherUsedWarrantyApplies}
                onChange={e => handleInputChange('otherUsedWarrantyApplies', e.target.checked)}
              />
              <CheckboxText>
                <StrongText>OTHER USED VEHICLE WARRANTY APPLIES.</StrongText>
              </CheckboxText>
            </WarrantyCheckboxRow>

            <WarrantyDocumentText>
              Ask the dealer for a copy of the warranty document and an explanation of warranty
              coverage, exclusions, and repair obligations.
            </WarrantyDocumentText>

            <ServiceContractRow>
              <SmallCheckbox
                type="checkbox"
                checked={formData.serviceContract}
                onChange={e => handleInputChange('serviceContract', e.target.checked)}
              />
              <CheckboxText>
                <StrongText>SERVICE CONTRACT.</StrongText>{' '}
                <LightText>
                  A service contract on this vehicle is available for an extra charge. Ask for
                  details about coverage, deductible, price, and exclusions. If you buy a service
                  contract within 90 days of your purchase of this vehicle,{' '}
                  <EmText>implied warranties</EmText> under your state's laws may give you
                  additional rights.
                </LightText>
              </CheckboxText>
            </ServiceContractRow>
          </NonDealerWarrantiesSection>

          <AdditionalInfoSection>
            <SectionTitle>
              ASK THE DEALER IF YOUR MECHANIC CAN INSPECT THE VEHICLE ON OR OFF THE LOT.
            </SectionTitle>

            <InfoText>
              <StrongText>
                OBTAIN A VEHICLE HISTORY REPORT AND CHECK FOR OPEN SAFETY RECALLS.
              </StrongText>{' '}
              For information on how to obtain a vehicle history report, visit ftc.gov/usedcars. To
              check for open safety recalls, visit safercar.gov. You will need the vehicle
              identification number (VIN) shown above to make use of the resources on these sites.
            </InfoText>

            <InfoText>
              <StrongText>SEE OTHER SIDE</StrongText> for important additional information,
              including a list of major defects that may occur in used motor vehicles.
            </InfoText>

            <InfoText>
              Si el concesionario gestiona la venta en español, pídale una copia de la Guía del
              Comprador en español.
            </InfoText>
          </AdditionalInfoSection>
        </FormContainer>

        <ButtonContainer>
          <BackButton onClick={handleBack}>Back to Dealer</BackButton>
          <PrintButton onClick={handlePrint}>Print Form</PrintButton>
          <GenerateButton onClick={handleGenerate}>Generate PDF</GenerateButton>
        </ButtonContainer>
      </PageContainer>
    </>
  );
}
